import { notFound, redirect } from "@tanstack/react-router";
import { buildHref, matchRoute } from "@softmax/web-addresses";
import { requireGameContext } from "@/features/game/data/index";
import { getActualViewer } from "@/data/viewer";
import { addressesOf } from "@/features/preview/state";
import {
  previewStartContent,
  viewerHasLiveEntry,
  viewerHasLiveEntryInLeague,
} from "@/features/preview/data/index";

import { onePagerCoworldOf } from "../onepager/coworlds";
import { startPreview } from "./preview/dev-stage";
import { emptyStartProgress } from "./progress/start-progress";
import type { StartPageData } from "./start.types";
export async function readStartPage({
  signal,
  href,
}: {
  readonly signal?: AbortSignal | undefined;
  readonly href: string;
}): Promise<StartPageData> {
  const url = new URL(href, window.location.href);

  if (matchRoute(url.pathname).name !== "game.start") throw notFound();

  const context = await requireGameContext({ signal, href });
  const params = {
    coworld: context.address.slug,
    league: context.league?.requestedSlug ?? undefined,
  };

  const address = addressesOf({ params, context });

  if (!params.league && onePagerCoworldOf(context.corpusSlug)) {
    throw redirect({
      href: buildHref({
        name: "game.play",
        game: context.address,
        search: url.searchParams,
      }),
    });
  }

  const [content, viewer] = await Promise.all([
    previewStartContent({ signal, address: address.url, context }),
    getActualViewer({ signal }),
  ]);

  if (!content) throw notFound();

  const startPath = buildHref({ name: "game.start", ...content.route });

  if (url.pathname !== startPath)
    throw redirect({ href: `${startPath}${url.search}` });

  const readMembership = params.league
    ? viewerHasLiveEntryInLeague
    : viewerHasLiveEntry;
  const isMember = viewer
    ? await readMembership({ signal, slug: address.corpus, context })
    : false;

  if (isMember) {
    throw redirect({
      href: buildHref({
        name: "game.play",
        ...content.route,
        search: url.searchParams,
      }),
    });
  }

  const preview = startPreview({
    search: url.searchParams,
    nodeEnv: import.meta.env.MODE,
    flagsOn: "",
  });
  const initialProgress =
    preview?.progress ?? (viewer ? null : emptyStartProgress());
  const signInHref = buildHref({
    name: "signIn",
    search: new URLSearchParams({ redirect_url: `${startPath}${url.search}` }),
  });
  return {
    title: `Get your cogent running in ${content.coworldName}${preview?.demo ? " · Demo" : ""}`,
    canonicalHref: startPath,
    initialProgress,
    wizard: {
      previewSlug: content.route.game.slug,
      route: content.route,
      corpusSlug: address.corpus,
      leagueName: content.leagueName,
      doorName: content.name,
      signInHref,
      search: url.search,
      playMdCommand: { text: content.command, href: content.commandHref },
      devSeeded: preview !== null,
      demo: preview?.demo ?? null,
      authFailed: preview?.fail === "auth",
      playerFailed: preview?.fail === "player",
      pollFailed: preview?.fail === "poll",
    },
  };
}
